/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Hook for fetching notifications
 */

import {
  createApiClient,
  isApiFailure,
  type ApiResult,
} from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import {
  buildNotificationSearchParams,
  filterNotificationMentions,
  filterNotificationSearch,
} from "@/lib/utils/notifications/filters";
import { useCallback } from "react";
import type { NotificationRow, UseNotificationsOptions } from "./types";

interface UseNotificationsFetchOptions {
  auth: {
    userId: string | null | undefined;
    getToken: () => Promise<string | null>;
    organizationId: string | null | undefined;
  };
  pushNotificationsEnabled: boolean;
  options: UseNotificationsOptions;
  setNotifications: (notifications: NotificationRow[]) => void;
  setUnreadCount: (count: number) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/**
 * Hook for fetching notifications
 */
export function useNotificationsFetch({
  auth,
  pushNotificationsEnabled,
  options,
  setNotifications,
  setUnreadCount,
  setIsLoading,
  setError,
}: UseNotificationsFetchOptions) {
  const fetchNotifications = useCallback(async () => {
    if (!auth.userId || !pushNotificationsEnabled) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = buildNotificationSearchParams({
        limit: options.limit,
        unreadOnly: options.unreadOnly,
        type: options.type,
        startDate: options.startDate,
        endDate: options.endDate,
      });

      const api = createApiClient({
        getToken: auth.getToken,
        getOrganizationId: () => auth.organizationId ?? undefined,
        autoRetry429: true,
      });
      const res = await api.get<NotificationRow[]>(
        `/api/notifications?${params}`,
      );
      if (isApiFailure(res)) {
        if (res.status === 404) {
          setNotifications([]);
          setUnreadCount(0);
          setError(null);
          return;
        }
        throw new Error(res.message);
      }
      let items: NotificationRow[] = Array.isArray(res.data) ? res.data : [];

      // Convert Json data field to Record for filter functions
      const itemsForFiltering = items.map((item) => ({
        ...item,
        data:
          item.data &&
          typeof item.data === "object" &&
          !Array.isArray(item.data)
            ? (item.data as Record<string, unknown>)
            : null,
      })) as Array<NotificationRow & { data: Record<string, unknown> | null }>;

      // Apply client-side filters
      if (options.mentionsOnly && auth.userId) {
        const filtered = filterNotificationMentions(
          itemsForFiltering,
          auth.userId,
        );
        items = filtered as NotificationRow[];
      }

      if (options.search) {
        const toFilter =
          options.mentionsOnly && auth.userId
            ? itemsForFiltering
            : itemsForFiltering;
        const filtered = filterNotificationSearch(toFilter, options.search);
        items = filtered as NotificationRow[];
      }

      setNotifications(items);

      // Fetch unread count separately for accurate badge counts
      const unreadRes: ApiResult<NotificationRow[]> = await api.get(
        "/api/notifications?unread_only=true",
      );
      if (isApiFailure(unreadRes)) {
        if (unreadRes.status === 404) {
          setUnreadCount(0);
        } else {
          toastFromApiFailure(
            "Failed to fetch unread notifications",
            unreadRes,
          );
        }
      } else {
        const arr = Array.isArray(unreadRes.data) ? unreadRes.data : [];
        setUnreadCount(arr.length);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, [
    auth.userId,
    auth.getToken,
    auth.organizationId,
    pushNotificationsEnabled,
    options.limit,
    options.unreadOnly,
    options.type,
    options.startDate,
    options.endDate,
    options.mentionsOnly,
    options.search,
    setNotifications,
    setUnreadCount,
    setIsLoading,
    setError,
  ]);

  return { fetchNotifications };
}
